import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Accordion, AccordionDetails, AccordionSummary } from '../Accordion/Accordion';
import { bulkUserRebalancing } from '@pages/Preferences/api';
import { useAsyncData } from '@shared/react';
import { usePagination2 } from '@features/Pagination/usePagination';
import { Paginator } from '@features/Pagination';
import { Loader } from '@shared/UI';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import './BulkRebalancingModal.scss';

interface BulkRebalancingContentProps {
    setDataGetter?: React.Dispatch<React.SetStateAction<() => any[]>>;
}

const BulkRebalancingModal: React.FC<BulkRebalancingContentProps> = ({ setDataGetter }) => {
    const [expanded, setExpanded] = useState<string | false>(false);
    const userStorage = localStorage.getItem('User');
    let userObject: any = null;
    userObject = JSON.parse(userStorage ? userStorage : '');
    const pageSizes = [10, 20, 30, 40, 50];
    const [allData, setAllData] = useState<any[]>([]);

    const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();

    const { data: bulkRebalancingList, loading: holdingLoading } = useAsyncData(
        async () => {
            if (userObject?.id && allData.length === 0) {
                const items = await bulkUserRebalancing(userObject.id);
                setAllData(items || []);
                setTotalCount(items?.length || 0);
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                return items?.slice(startIndex, endIndex) || [];
            } else {
                // Use already-fetched allData
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                setTotalCount(allData.length);
                return allData.slice(startIndex, endIndex);
            }
        },
        [],
        [userObject?.id, pageSize, page, allData.length],
    );

    useEffect(() => {
        if (setDataGetter) {
            setDataGetter(() => () => bulkRebalancingList); // Proper function return
        }
    }, [bulkRebalancingList, setDataGetter]);

    const formatLargeNumber = useCallback((value: number) => {
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
        return `$${value}`;
    }, []);

    if (holdingLoading) {
        return <Loader />;
    }

    return (
        <div>
            {bulkRebalancingList?.map((item: any, index: any) => {
                const panelId = `panel-${index}`;
                return (
                    <Accordion
                        key={panelId}
                        expanded={expanded === panelId}
                        onChange={handleChange(panelId)}
                        className={index === bulkRebalancingList.length - 1 ? 'last-accordion' : ''}
                    >
                        <AccordionSummary>
                            <Typography>
                                {item?.user?.firstName} {item?.user?.lastName}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>
                                <div>
                                    {item?.rebalancingReports?.length > 0 && (
                                        <div className='bulk-buy-sell-graph'>
                                            <div className='losses-section'>
                                                <div className='losses-title'>Securities Graph</div>
                                                <div className='losses-legend'>
                                                    <div className='losses-legend-item'>
                                                        <span className='losses-legend-color losses-realized'></span>
                                                        <span className='losses-legend-label'>Current Value</span>
                                                    </div>
                                                    <div className='losses-legend-item'>
                                                        <span className='losses-legend-color losses-unrealized'></span>
                                                        <span className='losses-legend-label'>Rebalanced Value</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='stock-chart'>
                                                <ResponsiveContainer
                                                    width='100%'
                                                    height={150}
                                                >
                                                    <BarChart
                                                        data={item?.rebalancingReports}
                                                        margin={{ top: 10, right: 10, left: -20, bottom: -10 }}
                                                    >
                                                        <CartesianGrid
                                                            vertical={false}
                                                            strokeDasharray='3 3'
                                                            stroke='rgba(0, 0, 0, 0.1490196078)'
                                                        />
                                                        <XAxis
                                                            dataKey='symbol'
                                                            axisLine={false}
                                                            tickLine={false}
                                                            interval={0}
                                                            tick={{ fontSize: 10 }}
                                                        />
                                                        <YAxis
                                                            axisLine={false}
                                                            tickLine={false}
                                                            tickFormatter={formatLargeNumber}
                                                            tick={{ fontSize: 10 }}
                                                        />
                                                        <Tooltip
                                                            formatter={(value: number, name: string) => {
                                                                return [`$${value.toLocaleString()}`, name];
                                                            }}
                                                            labelFormatter={(label: string) => `${label}`}
                                                            cursor={{ fill: 'transparent' }}
                                                        />
                                                        <Bar
                                                            dataKey='currentValue'
                                                            fill='#0066A6'
                                                            name='Current Value'
                                                            barSize={12}
                                                            radius={[6, 6, 6, 6]}
                                                        />
                                                        <Bar
                                                            dataKey='rebalancedValue'
                                                            fill='#FF9E69'
                                                            name='Rebalanced Value'
                                                            barSize={12}
                                                            radius={[6, 6, 6, 6]}
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}
                                    {item?.rebalancingReports?.length > 0 && (
                                        <div>
                                            <table className='bulk-rebalancing-table'>
                                                <thead>
                                                    <tr>
                                                        <th>Ticker</th>
                                                        <th>Current Value</th>
                                                        <th>Rebalanced Value</th>
                                                        <th>Gain/Loss</th>
                                                        <th>Cash</th>
                                                        <th>Share to Buy/Sell</th>
                                                    </tr>
                                                </thead>
                                            </table>

                                            <div className='table-body-wrapper'>
                                                <table className='bulk-rebalancing-table'>
                                                    <tbody>
                                                        {item?.rebalancingReports?.length < 1 ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={4}
                                                                    style={{ padding: 0 }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            height: '250px',
                                                                            textAlign: 'center',
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src='/NoData.png'
                                                                            alt='No data'
                                                                            style={{
                                                                                maxWidth: '200px',
                                                                                opacity: 0.7,
                                                                                marginBottom: '1rem',
                                                                            }}
                                                                        />
                                                                        <span
                                                                            style={{
                                                                                fontSize: '1rem',
                                                                                color: '#6b7280',
                                                                            }}
                                                                        >
                                                                            No data available
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            item?.rebalancingReports?.map(
                                                                (investment: any, index: number) => (
                                                                    <tr key={index}>
                                                                        <td>{investment?.symbol}</td>
                                                                        <td>
                                                                            <div className='grey-text-buy'>
                                                                                $
                                                                                {Math.abs(
                                                                                    investment?.currentValue,
                                                                                ).toFixed(2)}
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className='grey-text-buy'>
                                                                                $
                                                                                {Math.abs(
                                                                                    investment?.rebalancedValue,
                                                                                ).toFixed(2)}
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <span
                                                                                style={{
                                                                                    color:
                                                                                        investment?.gainLoss < 0
                                                                                            ? '#EB3F3C'
                                                                                            : '#5ECF82',
                                                                                }}
                                                                            >
                                                                                $
                                                                                {Math.abs(investment?.gainLoss).toFixed(
                                                                                    2,
                                                                                )}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <div className='grey-text-buy'>
                                                                                {' '}
                                                                                {investment?.symbol !== 'Cash' ? (
                                                                                    <>
                                                                                        {investment?.cashImpact > 0
                                                                                            ? 'Created:'
                                                                                            : 'Required:'}{' '}
                                                                                        $
                                                                                        {Math.abs(
                                                                                            investment?.cashImpact,
                                                                                        ).toFixed(2)}{' '}
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        {investment?.cashImpact < 0
                                                                                            ? 'Created:'
                                                                                            : 'Required:'}{' '}
                                                                                        $
                                                                                        {Math.abs(
                                                                                            investment?.cashImpact,
                                                                                        ).toFixed(2)}{' '}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <div className='grey-text-buy'>
                                                                                {' '}
                                                                                <div className='bold-black-text'>
                                                                                    {investment?.quantityChange < 0
                                                                                        ? 'Sell '
                                                                                        : 'Buy '}{' '}
                                                                                </div>
                                                                                {Math.abs(
                                                                                    investment?.quantityChange,
                                                                                ).toFixed(2)}{' '}
                                                                                Shares
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ),
                                                            )
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                    {item?.rebalancingReports?.length === 0 && (
                                        <div className='no-adjustment-text'>No Adjustments Required</div>
                                    )}
                                </div>
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
            {paginationOptions && bulkRebalancingList?.length > 0 && (
                <div className='paginator-bulk-rebalancing'>
                    <Paginator
                        {...paginationOptions}
                        pageSizes={pageSizes}
                    />
                </div>
            )}
            {bulkRebalancingList?.length === 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '490px',
                        textAlign: 'center',
                    }}
                >
                    <img
                        src='/NoData.png'
                        alt='No data'
                        style={{
                            maxWidth: '200px',
                            opacity: 0.7,
                            marginBottom: '1rem',
                        }}
                    />
                    <span style={{ fontSize: '1rem', color: '#6b7280' }}>No data available</span>
                </div>
            )}
        </div>
    );
};

export default BulkRebalancingModal;
